"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import type { Drop as DropType, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { ImageScale } from "@/helpers/image.helpers";
import { useMemo } from "react";
import DropContext from "./DropContext";
import { DropLocation } from "./drop.types";
import type { DropIdentityMode, DropInteractionParams, DropTimestampLayout } from "./drop.types";
import ParticipationDrop from "./participation/ParticipationDrop";
import WaveDrop from "./WaveDrop";
import WinnerDrop from "./winner/WinnerDrop";
export type { DropInteractionParams } from "./drop.types";
export { DropLocation } from "./drop.types";
interface DropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: DropType | null;
  readonly nextDrop: DropType | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly parentContainerRef?: React.RefObject<HTMLElement | null> | undefined;
  readonly wrapContentOnly?: (content: React.ReactNode) => React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly inlineAuthorOnDesktop?: boolean | undefined;
  readonly mediaImageScale?: ImageScale | undefined;
  readonly fullWidthMedia?: boolean | undefined;
  readonly fullWidthLinkPreviews?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
export default function Drop(t0) {
  const $ = _c(89);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    parentContainerRef,
    wrapContentOnly,
    footer,
    identityMode,
    timestampLayout,
    showInteractions: t1,
    inlineAuthorOnDesktop,
    mediaImageScale,
    fullWidthMedia,
    fullWidthLinkPreviews,
    winningThreshold,
    winningThresholdMinDurationMs,
    isVotingClosed: t2,
    isVotingControlsLocked: t3,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const showInteractions = t1 === undefined ? true : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const canOpenDrop = drop.drop_type !== ApiDropType.Chat || location !== DropLocation.WAVE;
  const openDropContentClick = canOpenDrop ? onDropContentClick : undefined;
  let t4;
  if ($[0] !== activeDrop || $[1] !== drop || $[2] !== embedDepth || $[3] !== embedPath || $[4] !== footer || $[5] !== fullWidthLinkPreviews || $[6] !== fullWidthMedia || $[7] !== identityMode || $[8] !== inlineAuthorOnDesktop || $[9] !== isVotingClosed || $[10] !== isVotingControlsLocked || $[11] !== location || $[12] !== maxEmbedDepth || $[13] !== mediaImageScale || $[14] !== onQuoteClick || $[15] !== onReply || $[16] !== openDropContentClick || $[17] !== parentContainerRef || $[18] !== quotePath || $[19] !== showInteractions || $[20] !== showReplyAndQuote || $[21] !== showWaveInfo || $[22] !== timestampLayout || $[23] !== winningThreshold || $[24] !== winningThresholdMinDurationMs) {
    t4 = <ParticipationDrop drop={drop} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} onReply={onReply} onQuoteClick={onQuoteClick} onDropContentClick={openDropContentClick} showReplyAndQuote={showReplyAndQuote} parentContainerRef={parentContainerRef} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} inlineAuthorOnDesktop={inlineAuthorOnDesktop} mediaImageScale={mediaImageScale} fullWidthMedia={fullWidthMedia} fullWidthLinkPreviews={fullWidthLinkPreviews} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[0] = activeDrop;
    $[1] = drop;
    $[2] = embedDepth;
    $[3] = embedPath;
    $[4] = footer;
    $[5] = fullWidthLinkPreviews;
    $[6] = fullWidthMedia;
    $[7] = identityMode;
    $[8] = inlineAuthorOnDesktop;
    $[9] = isVotingClosed;
    $[10] = isVotingControlsLocked;
    $[11] = location;
    $[12] = maxEmbedDepth;
    $[13] = mediaImageScale;
    $[14] = onQuoteClick;
    $[15] = onReply;
    $[16] = openDropContentClick;
    $[17] = parentContainerRef;
    $[18] = quotePath;
    $[19] = showInteractions;
    $[20] = showReplyAndQuote;
    $[21] = showWaveInfo;
    $[22] = timestampLayout;
    $[23] = winningThreshold;
    $[24] = winningThresholdMinDurationMs;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  let t5;
  if ($[26] !== activeDrop || $[27] !== drop || $[28] !== dropViewDropId || $[29] !== embedDepth || $[30] !== embedPath || $[31] !== footer || $[32] !== fullWidthLinkPreviews || $[33] !== fullWidthMedia || $[34] !== identityMode || $[35] !== inlineAuthorOnDesktop || $[36] !== location || $[37] !== maxEmbedDepth || $[38] !== mediaImageScale || $[39] !== nextDrop || $[40] !== onQuoteClick || $[41] !== onReply || $[42] !== onReplyClick || $[43] !== openDropContentClick || $[44] !== parentContainerRef || $[45] !== previousDrop || $[46] !== quotePath || $[47] !== showInteractions || $[48] !== showReplyAndQuote || $[49] !== showWaveInfo || $[50] !== timestampLayout || $[51] !== winningThreshold) {
    t5 = <WinnerDrop drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} dropViewDropId={dropViewDropId} onReply={onReply} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={openDropContentClick} showReplyAndQuote={showReplyAndQuote} parentContainerRef={parentContainerRef} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} inlineAuthorOnDesktop={inlineAuthorOnDesktop} mediaImageScale={mediaImageScale} fullWidthMedia={fullWidthMedia} fullWidthLinkPreviews={fullWidthLinkPreviews} winningThreshold={winningThreshold} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[26] = activeDrop;
    $[27] = drop;
    $[28] = dropViewDropId;
    $[29] = embedDepth;
    $[30] = embedPath;
    $[31] = footer;
    $[32] = fullWidthLinkPreviews;
    $[33] = fullWidthMedia;
    $[34] = identityMode;
    $[35] = inlineAuthorOnDesktop;
    $[36] = location;
    $[37] = maxEmbedDepth;
    $[38] = mediaImageScale;
    $[39] = nextDrop;
    $[40] = onQuoteClick;
    $[41] = onReply;
    $[42] = onReplyClick;
    $[43] = openDropContentClick;
    $[44] = parentContainerRef;
    $[45] = previousDrop;
    $[46] = quotePath;
    $[47] = showInteractions;
    $[48] = showReplyAndQuote;
    $[49] = showWaveInfo;
    $[50] = timestampLayout;
    $[51] = winningThreshold;
    $[52] = t5;
  } else {
    t5 = $[52];
  }
  const t6 = previousDrop?.type === DropSize.FULL ? previousDrop : null;
  const t7 = nextDrop?.type === DropSize.FULL ? nextDrop : null;
  let t8;
  if ($[53] !== activeDrop || $[54] !== drop || $[55] !== dropViewDropId || $[56] !== embedDepth || $[57] !== embedPath || $[58] !== footer || $[59] !== fullWidthLinkPreviews || $[60] !== fullWidthMedia || $[61] !== identityMode || $[62] !== inlineAuthorOnDesktop || $[63] !== location || $[64] !== maxEmbedDepth || $[65] !== mediaImageScale || $[66] !== onQuoteClick || $[67] !== onReply || $[68] !== onReplyClick || $[69] !== openDropContentClick || $[70] !== quotePath || $[71] !== showInteractions || $[72] !== showReplyAndQuote || $[73] !== showWaveInfo || $[74] !== t6 || $[75] !== t7 || $[76] !== timestampLayout || $[77] !== wrapContentOnly) {
    t8 = <WaveDrop drop={drop} previousDrop={t6} nextDrop={t7} showWaveInfo={showWaveInfo} activeDrop={activeDrop} location={location} dropViewDropId={dropViewDropId} onReply={onReply} onReplyClick={onReplyClick} onQuoteClick={onQuoteClick} onDropContentClick={openDropContentClick} showReplyAndQuote={showReplyAndQuote} wrapContentOnly={wrapContentOnly} footer={footer} identityMode={identityMode} timestampLayout={timestampLayout} showInteractions={showInteractions} inlineAuthorOnDesktop={inlineAuthorOnDesktop} mediaImageScale={mediaImageScale} fullWidthMedia={fullWidthMedia} fullWidthLinkPreviews={fullWidthLinkPreviews} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[53] = activeDrop;
    $[54] = drop;
    $[55] = dropViewDropId;
    $[56] = embedDepth;
    $[57] = embedPath;
    $[58] = footer;
    $[59] = fullWidthLinkPreviews;
    $[60] = fullWidthMedia;
    $[61] = identityMode;
    $[62] = inlineAuthorOnDesktop;
    $[63] = location;
    $[64] = maxEmbedDepth;
    $[65] = mediaImageScale;
    $[66] = onQuoteClick;
    $[67] = onReply;
    $[68] = onReplyClick;
    $[69] = openDropContentClick;
    $[70] = quotePath;
    $[71] = showInteractions;
    $[72] = showReplyAndQuote;
    $[73] = showWaveInfo;
    $[74] = t6;
    $[75] = t7;
    $[76] = timestampLayout;
    $[77] = wrapContentOnly;
    $[78] = t8;
  } else {
    t8 = $[78];
  }
  let t9;
  if ($[79] !== t4 || $[80] !== t5 || $[81] !== t8) {
    t9 = {
      [ApiDropType.Participatory]: t4,
      [ApiDropType.Winner]: t5,
      [ApiDropType.Chat]: t8
    };
    $[79] = t4;
    $[80] = t5;
    $[81] = t8;
    $[82] = t9;
  } else {
    t9 = $[82];
  }
  const components = t9;
  let t10;
  if ($[83] !== drop || $[84] !== location) {
    t10 = {
      drop,
      location
    };
    $[83] = drop;
    $[84] = location;
    $[85] = t10;
  } else {
    t10 = $[85];
  }
  const memoizedValue = t10;
  const t11 = components[drop.drop_type];
  let t12;
  if ($[86] !== memoizedValue || $[87] !== t11) {
    t12 = <DropContext.Provider value={memoizedValue}>{t11}</DropContext.Provider>;
    $[86] = memoizedValue;
    $[87] = t11;
    $[88] = t12;
  } else {
    t12 = $[88];
  }
  return t12;
}